import { useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, formatLoginError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ShieldCheck, Loader2, Play } from "lucide-react";
import { PrivacyConsent } from "../components/PrivacyConsent";
import { GiniusWordmark } from "../components/GiniusWordmark";
import { InstallPrompt } from "../components/InstallPrompt";
import { PasswordInput } from "../components/PasswordInput";

export default function Login() {
  const { login, loginSelect, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [condominioId, setCondominioId] = useState("");
  const [condomini, setCondomini] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [privacyOk, setPrivacyOk] = useState(false);
  // Iter122bt · multi-account selector
  // Iter122ez · toggle visibilità password gestito da PasswordInput
  const [multiAccounts, setMultiAccounts] = useState(null);

  const switchTo = async (m) => {
    setError("");
    setMode(m);
    if (m === "register" && condomini.length === 0) {
      try {
        const { data } = await api.get("/condomini");
        setCondomini(data);
      } catch { /* requires auth; ignore - fallback handled below */ }
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (mode === "login") {
        const res = await login(identifier, password);
        // Iter122bt · se la login ritorna una lista di account, mostro il selettore
        if (res && res.multi_account) {
          setMultiAccounts(res);
          setLoading(false);
          return;
        }
      } else {
        if (!privacyOk) { setError("Devi autorizzare il trattamento dei dati per registrarti"); setLoading(false); return; }
        await register({ name, email, password, role: "condomino", condominio_id: condominioId || null, privacy_consenso: privacyOk });
      }
      navigate("/");
    } catch (err) {
      // Iter123k196 · usa formatLoginError per distinguere 401/403/429/5xx/network
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const scegliAccount = async (acc) => {
    if (!multiAccounts) return;
    setError(""); setLoading(true);
    try {
      await loginSelect(multiAccounts.identifier, multiAccounts.password, acc.user_id);
      navigate("/");
    } catch (err) {
      setError(formatLoginError(err));
    } finally {
      setLoading(false);
    }
  };

  const quickFill = (em, pw) => { setIdentifier(em); setPassword(pw); setMode("login"); };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left - brand */}
      <div className="hidden lg:flex flex-col justify-between bg-[#1A1818] text-white p-12 relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-20 bg-cover bg-center"
          style={{ backgroundImage: "url(https://images.pexels.com/photos/28295552/pexels-photo-28295552.jpeg)" }}
        />
        <div className="relative z-10">
          <div className="flex items-center gap-4 smoke-in">
            <span className="ginius-flip-wrap inline-block w-24 h-24 rounded-2xl shadow-lg ring-2 ring-[#D4AF5A]/60" style={{ perspective: "600px" }}>
              <img src="/ginius-emblem.png" alt="Ginius" className="w-24 h-24 rounded-2xl object-contain" />
              <span data-back className="rounded-2xl text-6xl" aria-hidden="true">G</span>
            </span>
            <div>
              <div className="font-brand font-bold text-5xl leading-none text-brand"
                style={{ WebkitTextStroke: "1.6px #D4AF5A", textShadow: "0 0 18px rgba(212,175,90,0.45)" }}>Ginius</div>
              <div className="text-white font-semibold text-lg mt-2">Gestione manutenzioni condominiali</div>
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl font-extrabold leading-tight mb-4">
            La app di gestione delle manutenzioni in condominio
          </h1>
          <p className="text-lg text-slate-300">
            Dalla manutenzione all&apos;affido al fornitore, fino alla chiusura con ricevuta e valutazione del servizio.
            Semplice, chiaro e sicuro.
          </p>
        </div>
        <div className="relative z-10 text-slate-300">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" /> Dati trattati nel rispetto del GDPR
          </div>
        </div>
      </div>

      {/* Right - form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="flex flex-col items-center mb-6" data-testid="login-logo">
            <GiniusWordmark className="text-7xl text-brand" goldBorder />
            <div className="text-brand italic text-lg mt-2 font-medium">Tu lo chiedi, lui lo fa!</div>
          </div>

          <h2 className="font-display text-3xl font-bold mb-1">
            {mode === "login" ? "Accedi" : "Registrati come condòmino"}
          </h2>
          <p className="text-slate-600 text-lg mb-6">
            {mode === "login" ? "Inserisci le tue credenziali" : "Crea il tuo accesso per inviare manutenzioni"}
          </p>

          {mode === "login" && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("scaduta") && (
            <div data-testid="session-expired-notice" className="mb-5 p-3 rounded-lg bg-amber-50 border-2 border-amber-200 text-amber-800 text-base font-medium">
              La sessione è scaduta dopo 3 ore di inattività. Effettua di nuovo l'accesso.
            </div>
          )}

          {/* Iter122bt · Selettore multi-account */}
          {multiAccounts && (
            <div className="mb-6 rounded-xl border-2 border-brand bg-brand/5 p-4 space-y-3" data-testid="multi-account-picker">
              <div>
                <h3 className="font-display font-bold text-slate-800 text-lg">Scegli lo spazio in cui accedere</h3>
                <p className="text-xs text-slate-600 mt-1">Sono stati trovati più account associati a questa email. Seleziona quello con cui vuoi entrare.</p>
              </div>
              {multiAccounts.accounts.map((a) => (
                <button key={a.user_id} type="button"
                        onClick={() => scegliAccount(a)} disabled={loading}
                        data-testid={`account-pick-${a.user_id}`}
                        className="w-full text-left p-3 rounded-lg border-2 border-slate-200 bg-white hover:border-brand hover:bg-brand/5 transition-colors disabled:opacity-50">
                  <div className="font-bold text-slate-800">{a.role_label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.studio_nome || a.tenant_nome || a.tenant_id}
                  </div>
                </button>
              ))}
              <button type="button" onClick={() => setMultiAccounts(null)}
                      className="w-full text-xs text-slate-500 underline pt-2" data-testid="multi-account-annulla">
                ← Annulla e riprova
              </button>
            </div>
          )}

          {!multiAccounts && (
          <form onSubmit={submit} className="space-y-5">
            {mode === "register" && (
              <div>
                <Label className="text-base font-semibold">Nome e cognome</Label>
                <Input data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                  required className="h-14 text-lg mt-1.5" placeholder="Mario Rossi" />
              </div>
            )}
            {mode === "login" ? (
              <div>
                <Label className="text-base font-semibold">Email o ID di accesso</Label>
                <Input data-testid="login-email-input" type="text" value={identifier} onChange={(e) => setIdentifier(e.target.value)}
                  required className="h-14 text-lg mt-1.5" placeholder="m.rossi oppure nome@email.it" />
              </div>
            ) : (
              <div>
                <Label className="text-base font-semibold">Email</Label>
                <Input data-testid="register-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required className="h-14 text-lg mt-1.5" placeholder="nome@email.it" />
              </div>
            )}
            <div>
              <Label className="text-base font-semibold">Password</Label>
              <PasswordInput data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
                required className="h-14 text-lg mt-1.5" placeholder="••••••••" />
            </div>
            {mode === "register" && condomini.length > 0 && (
              <div>
                <Label className="text-base font-semibold">Il tuo condominio</Label>
                <select data-testid="register-condominio-select" value={condominioId} onChange={(e) => setCondominioId(e.target.value)}
                  className="h-14 text-lg mt-1.5 w-full border-2 border-slate-300 rounded-lg px-3 bg-white">
                  <option value="">Seleziona...</option>
                  {condomini.map((c) => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}

            {mode === "register" && (
              <PrivacyConsent checked={privacyOk} onChange={setPrivacyOk} testid="register-privacy" />
            )}

            {error && (
              <div data-testid="auth-error" className="p-4 bg-red-50 border-2 border-red-200 text-red-800 rounded-lg text-base font-semibold">
                {error}
              </div>
            )}

            <Button data-testid="login-submit-button" type="submit" disabled={loading}
              className="w-full h-14 text-lg font-bold bg-brand hover:bg-brand-dark">
              {loading ? <Loader2 className="w-6 h-6 animate-spin" /> : (mode === "login" ? "Accedi" : "Registrati")}
            </Button>
          </form>
          )}

          <div className="mt-6 flex items-center justify-center gap-1.5">
            <button data-testid="toggle-mode-button" onClick={() => switchTo(mode === "login" ? "register" : "login")}
              className="text-brand font-semibold text-base hover:underline">
              {mode === "login" ? "Sei un condomino? Registrati" : "Hai già un account? Accedi"}
            </button>
            <InfoHint section="login-registrazione" />
          </div>

          {mode === "login" && (
            <div className="mt-3 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-base">
              <span className="inline-flex items-center gap-1.5">
                <button data-testid="goto-activate" onClick={() => navigate("/attiva")}
                  className="text-brand font-semibold hover:underline">Prima attivazione account</button>
                <InfoHint section="login-attivazione" />
              </span>
              <span className="hidden sm:inline text-slate-300">·</span>
              <button data-testid="goto-forgot" onClick={() => navigate("/password-dimenticata")}
                className="text-slate-600 font-semibold hover:underline">Password dimenticata?</button>
            </div>
          )}

          {mode === "login" && (
            <div className="mt-4 text-center">
              <button data-testid="goto-presentazione" onClick={() => navigate("/presentazione")}
                className="inline-flex items-center gap-2 text-brand font-bold text-base hover:underline">
                <Play className="w-4 h-4 fill-brand" /> Guarda la presentazione guidata
              </button>
            </div>
          )}

          {mode === "login" && (
            <div className="mt-6 flex justify-center">
              <InstallPrompt />
            </div>
          )}

          {mode === "login" && (process.env.REACT_APP_BACKEND_URL || "").includes("preview.emergentagent.com") && (
            <div className="mt-8 p-4 bg-[#FBEEF3] border-2 border-[#F0D9E1] rounded-lg">
              <div className="text-sm font-bold uppercase tracking-wide text-slate-600 mb-2">Accessi demo</div>
              <div className="space-y-2 text-sm">
                <button data-testid="demo-admin" onClick={() => quickFill("admin@amministrazionizzo.com", "Izzo2025!")} className="block text-left w-full hover:bg-blue-100 p-2 rounded">
                  <b>Studio:</b> admin@amministrazionizzo.com / Izzo2025!
                </button>
                <button data-testid="demo-condomino" onClick={() => quickFill("condomino@demo.it", "demo1234")} className="block text-left w-full hover:bg-blue-100 p-2 rounded">
                  <b>Condomino:</b> condomino@demo.it / demo1234
                </button>
                <button data-testid="demo-fornitore" onClick={() => quickFill("fornitore@demo.it", "demo1234")} className="block text-left w-full hover:bg-blue-100 p-2 rounded">
                  <b>Fornitore:</b> fornitore@demo.it / demo1234
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
